/* Copyright 2013 - 2024 Waiterio LLC */
import getApiUrl from '@multilocale/shared/getApiUrl.js'
import {
  getAccessToken,
  isAccessTokenExpired,
  setAccessToken,
} from './accessToken.js'
import {
  getRefreshToken,
  isRefreshTokenExpired,
  setRefreshToken,
} from './refreshToken.js'

// A dead refresh token is terminal: no retry can mint tokens again, the only
// way forward is a clean re-auth. Navigate the CURRENT tab to /logout —
// window.open here would be popup-blocked in async fetch continuations (no
// user activation) and, in the WKWebView shells, bounced to an external
// browser where the logout can't converge back into the app. Guarded to once
// per page lifetime: every in-flight request hits this simultaneously.
let routedToLogout = false

function routeToLogout() {
  if (typeof window !== 'undefined' && !routedToLogout) {
    routedToLogout = true
    window.location.href = '/logout'
  }
}

export default async function refreshAccessToken() {
  let accessToken = getAccessToken()

  if (accessToken && isAccessTokenExpired()) {
    if (isRefreshTokenExpired()) {
      routeToLogout()

      throw new Error('refresh token has expired')
    } else {
      let refreshToken = getRefreshToken()

      const url = `${getApiUrl()}/api/refresh-access-token`

      let result

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Token ${btoa(refreshToken)}`,
          },
        })

        if (!response.ok) {
          // Never rethrow the parsed body itself: a plain object interpolates
          // as "[object Object]" wherever it bubbles and is unreadable in
          // Error Tracking. Wrap it in a real Error instead.
          const body = await response.json().catch(() => ({}))

          // The server rejected the refresh token outright: a 401 (user
          // deleted, token revoked) or a `jwt expired` body (the token lapsed
          // server-side — the API maps that to a 500, but at THIS endpoint it
          // can only mean the refresh token itself is dead). Either way no
          // retry can recover — same terminal state as an expired refresh
          // token: route to /logout for a clean re-auth.
          if (
            response.status === 401 ||
            /jwt expired/i.test(body.message || '')
          ) {
            routeToLogout()

            const error = new Error(
              `refresh token rejected: ${
                body.message || response.statusText || response.status
              }`,
            )
            error.status = response.status
            error.cause = body
            throw error
          }

          const error = new Error(
            body.message || response.statusText || `${response.status}`,
          )
          error.status = body.status || response.status
          error.cause = body
          throw error
        }

        result = await response.json()
      } catch (error) {
        console.log(error)
        throw error
      }

      accessToken = result.accessToken
      refreshToken = result.refreshToken

      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
    }
  }

  return accessToken
}
